<script lang="ts">
	import type { SvelteAsr, i18n } from 'types/common'
	import type { HomeSalesOrders$result, HomeData$result } from '$houdini'
	import type { SalesOrderFilters } from './sales-order'

	import HomeFilter from '../HomeFilter.svelte'
	import { toLocaleDateString } from '@isoftdata/utility-date-time'
	import { format as formatCurrency } from '@isoftdata/utility-currency'
	import Checkbox from '@isoftdata/svelte-checkbox'
	import Select from '@isoftdata/svelte-select'
	import { calculatePercent } from '../home'
	import Table, { type Column, Td } from '@isoftdata/svelte-table'
	import { getContext } from 'svelte'
	import NoItemsForSelectedFilter from 'components/NoItemsForSelectedFilter.svelte'

	interface Props {
		asr: SvelteAsr
		salesOrders: HomeSalesOrders$result['salesOrders']['items']
		documentTypes: Array<string>
		stores: HomeData$result['stores']
		users: HomeData$result['userAccounts']
		userId: SalesOrderFilters['userId']
		storeId: SalesOrderFilters['storeId']
		showClosed: SalesOrderFilters['showClosed']
		documentType: SalesOrderFilters['documentType']
		dates: SalesOrderFilters['dates']
	}

	let {
		//
		asr,
		dates,
		salesOrders,
		documentTypes,
		stores,
		users,
		userId,
		storeId,
		showClosed,
		documentType,
	}: Props = $props()

	const { t: translate } = getContext<i18n>('i18next')

	const columns: Array<Column<(typeof displaySalesOrders)[number]>> = [
		{ property: 'storeId', name: translate('common:storeNumber', 'Store #'), numeric: true },
		{ property: 'salesOrderId', name: translate('home.salesOrder.salesOrderNumberColumn', 'SO #'), numeric: true },
		{ property: 'date', name: translate('common:date', 'Date'), defaultSortColumn: true, defaultSortDirection: 'DESC' },
		{ property: 'customerName', name: translate('common:customer', 'Customer') },
		{ property: 'internalComments', name: translate('common:internalComments', 'Internal Comments') },
		{ property: 'counterperson', name: translate('common:counterperson', 'Counterperson') },
		{ property: 'salesperson', name: translate('common:salesperson', 'Salesperson') },
		{
			property: 'totalQuantityReady',
			name: translate('home.salesOrder.quantityReadyColumn', 'Qty Ready'),
			numeric: true,
		},
		{ property: 'percentReady', name: translate('home.salesOrder.percentReadyColumn', '% Ready'), numeric: true },
		{ property: 'total', name: translate('common:total', 'Total'), numeric: true },
		{ property: 'balance', name: translate('common:balance', 'Balance'), numeric: true },
		{ property: 'documentType', name: translate('common:document', 'Document') },
		{
			property: 'purchaseOrderNumber',
			name: translate('home.purchaseOrder.purchaseOrderNumberColumn', 'PO #'),
			numeric: true,
		},
		{ property: 'truckNumber', name: translate('home.salesOrder.truckNumberColumn', 'Truck #'), numeric: true },
		{ property: 'shippingMethod', name: translate('home.salesOrder.shippingMethodColumn', 'Ship Via') },
	]

	function filterChange() {
		asr.go(
			null,
			{
				userId: userId || null, //undefined will cause the parameter default to kick in, so pass null instead
				storeId,
				showClosed,
				documentType,
				fromDate: dates?.from,
				toDate: dates?.to,
			},
			{ inherit: true },
		)
	}

	let displaySalesOrders = $derived(
		salesOrders.map(so => {
			return {
				...so,
				storeId: so.store?.id,
				customerName: so.billingAddress?.company || so.billingAddress?.contact || '',
				salesperson: so.salesperson?.name || '',
				counterperson: so.counterperson?.name || '',
				percentReady: calculatePercent(so.totalQuantityReady, so.totalQuantity),
				shippingMethod: so.shippingMethod?.name || '',
				documentType: so.documentType?.name || '',
				total: Number(so.total) || 0,
				balance: Number(so.balance) || 0,
			}
		}),
	)
</script>

<div class="row">
	<div class="col-md-3 col-lg-2">
		<HomeFilter
			{stores}
			{users}
			{filterChange}
			dateRangeSetting="defaultSalesOrderHistoryDateRange"
			bind:userId
			bind:storeId
			bind:dates
		>
			{#snippet first()}
				<Select
					label={translate('common:documentType', 'Document Type')}
					emptyText={translate('common:allDocumentTypes', 'All Document Types')}
					bind:value={documentType}
					onchange={() => filterChange()}
				>
					{#each documentTypes as type}
						<option value={type}>{type}</option>
					{/each}
				</Select>
			{/snippet}
			<Checkbox
				label={translate('common:showClosed', 'Show Closed')}
				bind:checked={showClosed}
				onchange={filterChange}
			/>
		</HomeFilter>
	</div>

	<div
		class="col-md-9 col-lg-10 tab-pane"
		id="sales"
		role="tab"
	>
		{#if displaySalesOrders.length > 0}
			<Table
				responsive
				{columns}
				perPageCount={15}
				rows={displaySalesOrders}
			>
				{#snippet children({ row })}
					<tr
						class="cursor-pointer"
						class:text-body-secondary={row.finalized}
						onclick={() => asr.go('app.sales-order', { salesOrderId: row.salesOrderId, storeId: row.storeId })}
					>
						<Td property="storeId">{row.storeId}</Td>
						<Td property="salesOrderId">{row.salesOrderId}</Td>
						<Td property="date">{toLocaleDateString(row.date)}</Td>
						<Td property="customerName">{row.customerName}</Td>
						<Td property="internalComments">{row.internalComments}</Td>
						<Td property="counterperson">{row.counterperson}</Td>
						<Td property="salesperson">{row.salesperson}</Td>
						<Td property="totalQuantityReady">{row.totalQuantityReady}/{row.totalQuantity}</Td>
						<Td
							property="percentReady"
							class={{ 'text-primary': row.percentReady === 100 }}>{row.percentReady}%</Td
						>
						<Td property="total">{formatCurrency(row.total)}</Td>
						<Td property="balance">{formatCurrency(row.balance)}</Td>
						<Td property="documentType">{row.documentType}</Td>
						<Td property="purchaseOrderNumber">{row.purchaseOrderNumber}</Td>
						<Td property="truckNumber">{row.truckNumber}</Td>
						<Td property="shippingMethod">{row.shippingMethod}</Td>
					</tr>
				{/snippet}
			</Table>
		{:else}
			<NoItemsForSelectedFilter />
		{/if}
	</div>
</div>
